import { z } from 'zod'
import { enumSchema, requestSchema, responseSchema, type EnumMatches } from './contract'
import type { components } from './openapi.gen'

/**
 * Contrato da API v2 consumida pelo FE: rotas + schemas zod (validação em runtime).
 *
 * Fonte de verdade: `src/api/openapi.json`, snapshot do `v2/openapi.json` do woctools-backend,
 * sincronizado com `npm run api:sync`, que também gera `openapi.gen.ts`. Cada schema zod
 * passa por `responseSchema`/`requestSchema` (`./contract.ts`): se o backend mudar o contrato,
 * `npm run typecheck` falha aqui. Features importam daqui e nunca declaram tipos de API.
 */

type Schemas = components['schemas']

// ---------------------------------------------------------------------------
// Rotas
// ---------------------------------------------------------------------------

export const METABASE_KINDS = ['dashboards', 'indicators'] as const
export type MetabaseKind = (typeof METABASE_KINDS)[number]

export const apiPaths = {
  me: '/api/v2/me',
  scheduleRequests: '/api/v2/schedules/requests',
  scheduleRequestCompanies: (scheduleId: string) =>
    `/api/v2/schedules/requests/${encodeURIComponent(scheduleId)}/companies`,
  scheduleRequestSellers: (scheduleId: string, companyId: string) =>
    `/api/v2/schedules/requests/${encodeURIComponent(scheduleId)}/companies/${encodeURIComponent(companyId)}/sellers`,
  scheduleAvailability: '/api/v2/schedules/availability',
  scheduleJobVersions: '/api/v2/schedules/job-versions',
  metabaseCatalog: (kind: MetabaseKind) => `/api/v2/metabase/${kind}`,
  metabaseItem: (kind: MetabaseKind, metabaseId: number) =>
    `/api/v2/metabase/${kind}/${encodeURIComponent(String(metabaseId))}`,
  metabaseSession: '/api/v2/metabase/govendas-session',
  metabaseAvailable: (kind: MetabaseKind) => `/api/v2/metabase/${kind}/available`,
  metabaseEmbedUrl: (kind: MetabaseKind, slug: string) =>
    `/api/v2/metabase/${kind}/${encodeURIComponent(slug)}/embed-url`,
} as const

const listOf = <T extends z.ZodTypeAny>(item: T) => z.object({ items: z.array(item) })

// ---------------------------------------------------------------------------
// Autorização (DEC-004)
// ---------------------------------------------------------------------------

export const CAPABILITIES = [
  'schedule:read',
  'schedule:create',
  'schedule:report',
  'metabase:read',
  'metabase:manage',
  'integration:read',
  'integration:run',
  'realtime:subscribe',
  'system:admin',
] as const satisfies readonly Schemas['Capability'][]

export type Capability = (typeof CAPABILITIES)[number]
/** A lista de capabilities do FE é exatamente a do contrato. */
export const CAPABILITIES_MATCH_CONTRACT: EnumMatches<Capability, Schemas['Capability']> = true

const capabilitySet = new Set<string>(CAPABILITIES)
export const isCapability = (value: string): value is Capability => capabilitySet.has(value)

export const PROFILES = ['viewer', 'operator', 'admin', 'service'] as const
type Profile = (typeof PROFILES)[number]
export const PROFILES_MATCH_CONTRACT: EnumMatches<Profile, Schemas['Profile']> = true
const isProfile = (value: string): value is Profile =>
  (PROFILES as readonly string[]).includes(value)

export const meSchema = responseSchema<Schemas['MeResponse']>()(
  z.object({
    user_id: z.string(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    username: z.string().nullable(),
    roles: z.array(z.string()),
    // Perfis/capabilities desconhecidos são descartados (o backend pode evoluir antes do FE).
    profiles: z.array(z.string()).transform((values) => values.filter(isProfile)),
    capabilities: z.array(z.string()).transform((values) => values.filter(isCapability)),
    is_service: z.boolean(),
    picture: z.string().nullish(),
  }),
)
export type Me = z.infer<typeof meSchema>

// ---------------------------------------------------------------------------
// Agendamentos
// ---------------------------------------------------------------------------

export const EXECUTION_STATUSES = [
  'waiting',
  'running',
  'success',
  'failed',
  'skipped',
  'unknown',
] as const satisfies readonly Schemas['ExecutionStatus'][]
export const executionStatusSchema = enumSchema<Schemas['ExecutionStatus']>()(
  z.enum(EXECUTION_STATUSES),
)
export type ExecutionStatus = z.infer<typeof executionStatusSchema>

/**
 * Etapas na ordem enviada pelo legado (rules antes de portfolio). A ordem vem do código, não
 * da ordem em que o usuário marca as etapas.
 */
export const WORKFLOW_STEPS = [
  'govendas-update',
  'journal-update',
  'clients-enrichment',
  'govendas-rules',
  'customer-portfolio',
  'data-processor',
  'govendas-engine',
] as const satisfies readonly Schemas['WorkflowStep'][]
export const workflowStepSchema = enumSchema<Schemas['WorkflowStep']>()(z.enum(WORKFLOW_STEPS))
export type WorkflowStep = z.infer<typeof workflowStepSchema>

export const JOB_KEYS = [
  'gu',
  'ce',
  'ju',
  'cp',
  'gr',
  'dp',
  'ge',
  'awdp',
  'awsp',
] as const satisfies readonly Schemas['JobKey'][]
export const jobKeySchema = enumSchema<Schemas['JobKey']>()(z.enum(JOB_KEYS))
export type JobKey = z.infer<typeof jobKeySchema>

export const statusCountersSchema = responseSchema<Schemas['StatusCounters']>()(
  z.object({
    total: z.number().int().default(0),
    waiting: z.number().int().default(0),
    running: z.number().int().default(0),
    success: z.number().int().default(0),
    failed: z.number().int().default(0),
    skipped: z.number().int().default(0),
  }),
)
export type StatusCounters = z.infer<typeof statusCountersSchema>

export const requesterSchema = responseSchema<Schemas['Requester']>()(
  z.object({
    email: z.string(),
    full_name: z.string().nullish(),
    short_name: z.string().nullish(),
    family_name: z.string().nullish(),
    picture_url: z.string().nullish(),
  }),
)
export type Requester = z.infer<typeof requesterSchema>

export const scheduleRequestSchema = responseSchema<Schemas['ScheduleRequestSummary']>()(
  z.object({
    schedule_id: z.string(),
    schedule_date: z.string().nullable(),
    schedule_status: executionStatusSchema,
    inserted_at: z.string().nullable(),
    updated_at: z.string().nullish(),
    requester_id: z.string().nullish(),
    requester: requesterSchema.nullish(),
    workflow_steps: z.array(z.string()).default([]),
    more_info: z.string().nullish(),
    number_of_companies: statusCountersSchema,
    number_of_sellers: statusCountersSchema,
    /** Link da execução no Argo, montado pelo backend (o FE não conhece hosts de infra). */
    workflow_url: z.string().nullish(),
  }),
)
export type ScheduleRequest = z.infer<typeof scheduleRequestSchema>
export const scheduleRequestListSchema = responseSchema<Schemas['ScheduleRequestList']>()(
  listOf(scheduleRequestSchema),
)

export const jobInfoSchema = responseSchema<Schemas['JobInfo']>()(
  z.object({
    key: z.string(),
    name: z.string(),
    status: executionStatusSchema,
  }),
)
export type JobInfo = z.infer<typeof jobInfoSchema>

export const companyStatusSchema = responseSchema<Schemas['CompanyStatus']>()(
  z.object({
    schedule_id: z.string(),
    company_id: z.string(),
    company_name: z.string().nullable(),
    company_status: executionStatusSchema,
    number_of_sellers: z.number().int().nullish(),
    jobs_info: z.array(jobInfoSchema).default([]),
    more_info: z.string().nullish(),
    updated_at: z.string().nullish(),
  }),
)
export type CompanyStatus = z.infer<typeof companyStatusSchema>
export const companyStatusListSchema = responseSchema<Schemas['CompanyStatusList']>()(
  listOf(companyStatusSchema),
)

export const sellerStatusSchema = responseSchema<Schemas['SellerStatus']>()(
  z.object({
    schedule_id: z.string(),
    company_id: z.string(),
    company_name: z.string().nullable(),
    seller_id: z.string(),
    seller_name: z.string().nullish(),
    seller_status: executionStatusSchema,
    number_of_clients: z.number().int().nullish(),
    more_info: z.string().nullish(),
    updated_at: z.string().nullish(),
  }),
)
export type SellerStatus = z.infer<typeof sellerStatusSchema>
export const sellerStatusListSchema = responseSchema<Schemas['SellerStatusList']>()(
  listOf(sellerStatusSchema),
)

export const availableSellerSchema = responseSchema<Schemas['AvailableSeller']>()(
  z.object({
    seller_id: z.string(),
    seller_name: z.string().nullable(),
    label: z.string(),
  }),
)
export const availableCompanySchema = responseSchema<Schemas['AvailableCompany']>()(
  z.object({
    /** gogeo_sname: chave usada no plano e em `available_to` do Metabase. */
    company_id: z.string(),
    company_code: z.string().nullable(),
    label: z.string(),
    sellers: z.array(availableSellerSchema).default([]),
  }),
)
export type AvailableCompany = z.infer<typeof availableCompanySchema>
export const availabilitySchema = responseSchema<Schemas['AvailabilityResponse']>()(
  z.object({ companies: z.array(availableCompanySchema) }),
)
export type Availability = z.infer<typeof availabilitySchema>

export const jobVersionsSchema = responseSchema<Schemas['JobVersionsResponse']>()(
  z.object({
    jobs: z.array(
      z.object({
        job: jobKeySchema,
        repository: z.string(),
        latest: z.string().nullish(),
        versions: z.array(
          z.object({
            tag: z.string(),
            pushed_at: z.string().nullish(),
            branch: z.string().nullish(),
          }),
        ),
      }),
    ),
  }),
)
export type JobVersions = z.infer<typeof jobVersionsSchema>

const idPattern = /^[A-Za-z0-9_.:@-]{1,128}$/
export const SCHEDULE_ID_PATTERN = /^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/

/** Corpo do POST /api/v2/schedules/requests (JSON único, no lugar dos headers do legado). */
export const createScheduleRequestSchema = requestSchema<Schemas['CreateScheduleRequest']>()(
  z.object({
    schedule_id: z.string().max(63).regex(SCHEDULE_ID_PATTERN).nullish(),
    mode: enumSchema<Schemas['ScheduleMode']>()(z.enum(['today', 'advanced'])).optional(),
    schedule_date: z.string().nullish(),
    customer_portfolio_date: z.string().nullish(),
    workflow_steps: z.array(workflowStepSchema).min(1).max(7).optional(),
    schedule_check: z.boolean().optional(),
    job_versions: z.record(jobKeySchema, z.string().regex(/^[A-Za-z0-9._-]{1,128}$/)).optional(),
    number_of_days_to_analyze: z.number().int().min(1).max(3650).optional(),
    number_of_months_to_analyze: z.number().int().min(0).max(120).optional(),
    plan: z
      .object({
        /** company_id → { seller_ids }; vazio = todas as empresas; seller_ids vazio = todos. */
        companies: z
          .record(
            z.string().regex(idPattern),
            z.object({ seller_ids: z.array(z.string().regex(idPattern)).optional() }),
          )
          .optional(),
      })
      .optional(),
  }),
)
export type CreateScheduleRequest = z.infer<typeof createScheduleRequestSchema>

export const scheduleRequestAcceptedSchema = responseSchema<Schemas['ScheduleRequestAccepted']>()(
  z.object({
    schedule_id: z.string(),
    schedule_status: executionStatusSchema.default('waiting'),
    workflow_name: z.string(),
    // B-01: o BE em produção (b2ac65c) não envia o campo; o RC-006 volta a enviar `false`.
    // Ausente = execução real (o 202 do Gerar não pode virar "fora do contrato" por isso).
    dry_run: z.boolean().optional(),
    resolved_versions: z.record(jobKeySchema, z.string()),
    workflow_url: z.string().nullish(),
  }),
)
export type ScheduleRequestAccepted = z.infer<typeof scheduleRequestAcceptedSchema>

// ---------------------------------------------------------------------------
// Metabase (dashboards e indicadores compartilham o mesmo formato)
// ---------------------------------------------------------------------------

export const METABASE_SLUG_PATTERN = /^[A-Za-z0-9_-]{1,128}$/

export const metabaseItemFieldsSchema = requestSchema<Schemas['MetabaseItemFields']>()(
  z.object({
    slug: z.string().regex(METABASE_SLUG_PATTERN),
    title: z.string().max(255),
    subtitle: z.string().max(255).nullish(),
    description: z.string().max(5000).nullish(),
    tutorial_link: z.string().max(2048).nullish(),
    version: z.string().max(64).nullish(),
    is_available_to_everybody: z.boolean().default(false),
    available_to: z.array(z.string()).default([]),
    role_whitelist: z.array(z.string()).default([]),
    deprecated: z.boolean().default(false),
    /** JSON livre (o legado guarda `[{name, required, default_value?}]`). */
    default_params: z.unknown().optional(),
    jwt_fields: z.array(z.string()).default([]),
    url_fields: z.array(z.string()).default([]),
  }),
)
export type MetabaseItemFields = z.infer<typeof metabaseItemFieldsSchema>

export const metabaseItemSchema = responseSchema<Schemas['MetabaseItem']>()(
  metabaseItemFieldsSchema.extend({
    metabase_id: z.number().int().min(1),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
  }),
)
export type MetabaseItem = z.infer<typeof metabaseItemSchema>
export const metabaseItemListSchema = responseSchema<Schemas['MetabaseItemList']>()(
  listOf(metabaseItemSchema),
)

export const metabaseUpsertResultSchema = responseSchema<Schemas['MetabaseUpsertResult']>()(
  z.object({
    created: z.boolean(),
    item: metabaseItemSchema,
  }),
)
export type MetabaseUpsertResult = z.infer<typeof metabaseUpsertResultSchema>

export const goVendasSessionSchema = responseSchema<Schemas['GoVendasSessionResponse']>()(
  z.object({
    session_token: z.string(),
    email: z.string().nullish(),
    expires_at: z.string().nullish(),
  }),
)
export type GoVendasSession = z.infer<typeof goVendasSessionSchema>

export const availableReportListSchema = responseSchema<Schemas['AvailableReportList']>()(
  listOf(
    z.object({ slug: z.string(), title: z.string().nullish(), subtitle: z.string().nullish() }),
  ),
)
export type AvailableReport = z.infer<typeof availableReportListSchema>['items'][number]

export const embedUrlSchema = responseSchema<Schemas['EmbedUrlResponse']>()(
  z.object({ url: z.string().url() }),
)

/** Corpos de requisição tipados direto do contrato. */
export type GoVendasSessionRequest = Schemas['GoVendasSessionRequest']
export type EmbedUrlRequest = Schemas['EmbedUrlRequest']
